// useNotificationChannels — the channel-config READ seam (contract 22 §9). The Notifications pane
// renders it; modules 13 (impact-analysis), 14 (workflow) and 17 (alerts) consume
// `availability`/`isAvailable` to decide whether a channel may be picked as a destination
// (14 §2 delegates channel *configuration* here).
//
// This file is the settled home. Ledger §B2 used to require extracting the read into a
// `shared/notifications` module; that was REVERSED 2026-08-06 once its load-bearing reason — an
// `08 → 22` edge inverting §A7 — proved not to exist in v4 (08 has no notification code; its v3
// export-recipient read was designed out). See the ledger row and contract 22 §18.4. The name is
// taken anyway: `shared/notifications` is the provider-icon module.
//
// State is per-call and component-local, matching §7 ("every pane's working data is
// component-local") and `useMembers`.
import { computed, ref, shallowRef, type ComputedRef } from 'vue'

import { NotificationChannelsApi } from '../api/NotificationChannelsApi'
import {
  CHANNEL_CATALOG,
  type ChannelAvailability,
  type ChannelConfig,
  type NotificationChannel,
  type NotificationInformationDto,
} from '../models/channel.types'
import type { SettingsPaneState } from '../models/settingsPane.types'
import { channelAvailability, channelConfig } from '../service/channelValidation'

import { useWorkspace } from '@/features/account'
import { ApiError } from '@/shared/http'

export type ChannelConfigMap = Record<NotificationChannel, ChannelConfig>

export interface UseNotificationChannelsOptions {
  api?: NotificationChannelsApi
}

export interface UseNotificationChannels {
  /** Every channel in `CHANNEL_CATALOG`, configured or not — never a partial map. */
  channels: ComputedRef<ChannelConfigMap>
  /** What 13/17 read: configured + enabled + the recipients, per channel. */
  availability: ComputedRef<ChannelAvailability[]>
  /** A channel is selectable as a destination iff it is configured AND enabled (17 §2). */
  isAvailable: (channel: NotificationChannel) => boolean
  recipients: (channel: NotificationChannel) => string[]
  /** 22's pane vocabulary. `empty` is never emitted: the catalog is four fixed channels, so a
   *  workspace with nothing configured still has four rows to configure — rendering an empty state
   *  there would hide the only controls that could fix it. `forbidden` here is the 403 degrade;
   *  the pane ORs in its own RBAC denial (§12). */
  state: ComputedRef<SettingsPaneState>
  error: ComputedRef<ApiError | null>
  /** Fetch (or re-fetch) the SELECTED workspace's channel config. Never throws — read `state`/`error`. */
  refresh: () => Promise<void>
  /**
   * Availability for ANY workspace, without touching this instance's pane state.
   *
   * The pane reads one workspace — the selected one — so the reactive state above is scoped to it.
   * Module 17 is not: an account-scoped alert list spans every workspace and must read each one's
   * channels (v3 did, `views/App/Tools/Alerts.vue:537`, inside `wsIds.map`). Before this existed
   * the only seam was the selected-workspace state, so 17 returned `[]` for every other workspace
   * and their alerts silently rendered with no channels.
   *
   * REJECTS with `ApiError` — unlike `refresh()`, which folds failure into `state`. A multi-workspace
   * caller wants per-workspace failure, and 17's `alertService` already records it as an
   * `AlertUnavailableSlice` rather than failing the whole read.
   */
  loadFor: (workspaceId: number | string) => Promise<ChannelAvailability[]>
}

export function useNotificationChannels(
  options: UseNotificationChannelsOptions = {},
): UseNotificationChannels {
  const api = options.api ?? new NotificationChannelsApi()
  const { workspaceId } = useWorkspace()

  const payload = shallowRef<NotificationInformationDto | null>(null)
  const pending = ref(false)
  const loaded = ref(false)
  const error = ref<ApiError | null>(null)

  const channels = computed<ChannelConfigMap>(
    () =>
      Object.fromEntries(
        CHANNEL_CATALOG.map((descriptor) => [
          descriptor.channel,
          channelConfig(payload.value, descriptor),
        ]),
      ) as ChannelConfigMap,
  )

  const availability = computed<ChannelAvailability[]>(() => channelAvailability(payload.value))

  const state = computed<SettingsPaneState>(() => {
    // A 403 is a first-class render, not an error banner (§12 "degraded, not broken").
    if (error.value) return error.value.kind === 'forbidden' ? 'forbidden' : 'error'
    return pending.value || !loaded.value ? 'loading' : 'ready'
  })

  async function refresh(): Promise<void> {
    const workspace = workspaceId.value
    if (workspace === undefined) {
      payload.value = null
      loaded.value = false
      return
    }

    pending.value = true
    error.value = null
    try {
      payload.value = await api.getInformation(workspace)
      loaded.value = true
    } catch (cause) {
      error.value = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
      payload.value = null
      loaded.value = false
    } finally {
      pending.value = false
    }
  }

  return {
    channels,
    availability,
    loadFor: async (workspace) => channelAvailability(await api.getInformation(workspace)),
    isAvailable: (channel) => {
      const config = channels.value[channel]
      return config.values.length > 0 && config.enabled
    },
    recipients: (channel) => channels.value[channel].values,
    state,
    error: computed(() => error.value),
    refresh,
  }
}
